
import axios from 'axios';

import {
  defineComponent,
  defineAsyncComponent,
  ref,
  reactive,
  computed,
  onMounted,
} from 'vue';

import type {
  Option,
  ErrorsMap,
  Endpoint,
  Feeds,
  Users,
  Campaigns,
} from '@/types';

import {
  ENTRIES,
  TRAFFIC_TYPE_POP,
  TRAFFIC_TYPE_PUSH,
  TRAFFIC_TYPE_VIDEO,
  TRAFFIC_TYPES,
  TRAFFIC_TYPE_MIX,
  TRAFFIC_CATEGORY_MAINSTREAM,
  TRAFFIC_CATEGORY_ADULT,
  TRAFFIC_CATEGORIES,
  REGION_EU,
  REGIONS,
  TARGETING_TYPE_DISABLED,
  TARGETING_TYPES,
  PROTOCOL_LEGACY,
  PROTOCOL_OPENRTB,
  PROTOCOL_DIRECT_LINK,
  PROTOCOLS,
  BID_SUBJECT_CLICK,
  BID_SUBJECT_VIEW,
  BID_SUBJECTS,
  BID_TYPE_PER_THOUSAND,
  BID_TYPES,
  PAYABLE_VIEW_REGISTRATION_ON_BILLING,
  PAYABLE_VIEW_REGISTRATION_ON_VIEW,
  PAYABLE_VIEW_REGISTRATIONS,
  ENDPOINT_MAX_QPS_0,
  ENDPOINT_MAX_QPS,
  ENDPOINT_STATUS_ENABLED,
  ENDPOINT_STATUSES,
  CUSTOM_RESPONSE_PROVIDERS,
  CUSTOM_RESPONSE_PROVIDER_EXOCLICK,
  BID_TYPE_PER_ONE,
} from '@/libs/consts';

import useUser from '@/composable/useUser';

import useEntries from '@/composable/useEntries';

import useEntriesNext from '@/composable/useEntries@next';

import { validate as validateUuid } from 'uuid';

export default defineComponent({
  components: {
    EndpointMeta: defineAsyncComponent(() => import('@/components/EndpointMeta.vue')),
    CustomRevshares: defineAsyncComponent(() => import('@/components/CustomRevshares.vue')),
    ShaveRules: defineAsyncComponent(() => import('@/components/ShaveRules.vue')),
    CustomBids: defineAsyncComponent(() => import('@/components/CustomBids.vue')),
    PusherStats: defineAsyncComponent(() => import('@/components/PusherStats.vue')),
  },
  setup() {
    const { activeUser: user } = useUser();

    const { getEntryByRoute } = useEntries();

    const {
      entriesLoading: projectsLoading,
      entries: projects,
      fetchEntries: fetchProjects,
    } = useEntries();

    const {
      entriesLoading: feedsLoading,
      entries: feeds,
      fetchEntriesViaGET: fetchFeeds,
    } = useEntriesNext<Feeds>('/api/v3/feeds/options?include=project&sort=status&paginate=false&with_unmanageable=true', ENTRIES);

    const {
      entriesLoading: advertisersLoading,
      entries: advertisers,
      fetchEntriesViaGET: fetchAdvertisers,
    } = useEntriesNext<Users>('/api/v3/users/options?include=project&sort=status&paginate=false&with_unmanageable=true', ENTRIES);

    const {
      entriesLoading: campaignsLoading,
      entries: campaigns,
      fetchEntriesViaGET: fetchCampaigns,
    } = useEntriesNext<Campaigns>('/api/v3/campaigns/options?include=project,user&sort=status&paginate=false&with_unmanageable=true', ENTRIES);

    const {
      entriesLoading: countriesLoading,
      entries: countries,
      fetchEntries: fetchCountries,
    } = useEntries();

    const endpoint = reactive<Endpoint>({
      site_id: null,
      direct: false,
      custom_response_provider: null,
      max_qps: ENDPOINT_MAX_QPS_0,
      get_ip_and_user_agent_from_headers: false,
      click_host_eu: null,
      click_host_us: null,
      click_host_sg: null,
      click_host_in: null,
      hide_pseudosite_host_in_click_url: false,
      replace_feeds_responses: false,
      show_ad_id: false,
      bid_subject: BID_SUBJECT_CLICK,
      payable_view_registration: PAYABLE_VIEW_REGISTRATION_ON_VIEW,
      revshare: 60,
      revshare_by_countries_ids: [],
      revshare_by_subids_uuids: [],
      revshare_by_referrers_uuids: [],
      shave: 0,
      shave_rules: [],
      pay_filtered_click_chance: 0,
      min_bid_override: null,
      campaign_bid_divider: null,
      fixed_bid: null,
      fixed_bid_by_country: [],
      allow_referrer_mismatch: false,
      disable_decline_non_effective_subids: false,
      subids_checking_delay_to: null,
      passback_url: null,
      allow_feeds: false,
      allow_campaigns: false,
      jerk_feeds_retries: null,
      only_mainstream_coverage: false,
      sensitive: false,
      targeting_by_feeds_ids: TARGETING_TYPE_DISABLED,
      feeds_ids: [666],
      targeting_by_advertisers_ids: TARGETING_TYPE_DISABLED,
      advertisers_ids: [],
      targeting_by_campaigns: TARGETING_TYPE_DISABLED,
      campaigns_ids: [],
      pop_frequency_seconds: 60,
      pop_targeting_by_countries: TARGETING_TYPE_DISABLED,
      pop_countries_ids: [],
      push_delay_to_first_sending: 30,
      push_delay_to_sending: 600,
      push_delay_to_sending_mature: 0,
      push_max_sendings: 300,
      push_max_age: 7,
      push_quiet_hour: false,
      comment: null,
      status: ENDPOINT_STATUS_ENABLED,
    } as unknown as Endpoint);

    const endpointErrorsMap = ref<ErrorsMap>({});

    const isPop = computed(() => endpoint.traffic_type === TRAFFIC_TYPE_POP);

    const isPush = computed(() => endpoint.traffic_type === TRAFFIC_TYPE_PUSH);

    const isVideo = computed(() => endpoint.traffic_type === TRAFFIC_TYPE_VIDEO);

    const isLegacy = computed(() => endpoint.protocol === PROTOCOL_LEGACY);

    const isDirectLink = computed(() => endpoint.protocol === PROTOCOL_DIRECT_LINK);

    const usersParams = computed(() => {
      const include = [];

      if (!endpoint.project_id) {
        include.push('project');
      }

      return {
        include: include.join(','),
        'filter[project_id]': endpoint.project_id,
      };
    });

    const sitesParams = computed(() => {
      const include = [];

      if (!endpoint.project_id) {
        include.push('project');
      }

      if (!endpoint.user_id) {
        include.push('user');
      }

      return {
        include: include.join(','),
        'filter[project_id]': endpoint.project_id,
        'filter[user_id]': endpoint.user_id,
      };
    });

    const trafficTypes = computed(() => TRAFFIC_TYPES.map((type) => {
      if (
        type.value !== TRAFFIC_TYPE_POP
        && isDirectLink.value) {
        return { ...type, disabled: true };
      }

      return type;
    }));

    const protocols = computed(() => PROTOCOLS.map((protocol) => {
      if (
        isDirectLink.value
        && endpoint.traffic_type
        && !isPop.value) {
        return { ...protocol, disabled: true };
      }

      return protocol;
    }));

    const regions = computed(() => REGIONS.map((region) => {
      if (
        region.value !== REGION_EU
        && isDirectLink.value) {
        return { ...region, disabled: true };
      }

      return region;
    }));

    const declinedSubids = ref<string[]>([]);

    const declinedSubidsLoading = ref<boolean>(false);

    const declinedSubidsAsStringSeparators = [
      {
        value: '\n',
        translate_key: 'new_line',
      },
      {
        value: ',',
        translate_key: 'comma',
      },
    ] as Option[];

    const declinedSubidsAsStringSeparator = ref<string>(declinedSubidsAsStringSeparators[0].value as string);

    const declinedSubidsAsString = computed<string>(() => declinedSubids.value.join(declinedSubidsAsStringSeparator.value));

    const showDeclinedSubids = ref<boolean>(false);

    const fetchAdvertisersWrapper = async (): Promise<void> => fetchAdvertisers({
      'filter[project_id]': user.isSuperAdmin() ? endpoint.project_id : null,
    });

    const fetchCampaignsWrapper = async (): Promise<void> => fetchCampaigns({
      'filter[project_id]': user.isSuperAdmin() ? endpoint.project_id : null,
      'filter[traffic_type]': TRAFFIC_TYPE_MIX.get(endpoint.traffic_type).join(','),
    });

    const fetchFeedsWrapper = async (): Promise<void> => fetchFeeds({
      'filter[project_id]': user.isSuperAdmin() ? endpoint.project_id : null,
      // Для эндпоинта отображаем максимально доступное покрытие
      'filter[traffic_type]': TRAFFIC_TYPE_MIX.get(endpoint.traffic_type).join(','),
    });

    const fetchEndpoint = async (): Promise<void> => {
      const entry = await getEntryByRoute('/api/endpoint/getEndpointById');

      if (entry) {
        Object.assign(endpoint, entry);
      }

      if (user.isStaff()) {
        Promise.all([
          user.isSuperAdmin() ? fetchProjects('/api/project/getProjects') : Promise.resolve(),
          fetchFeedsWrapper(),
          fetchAdvertisersWrapper(),
          fetchCampaignsWrapper(),
        ]);
      }

      Promise.all([
        fetchCountries('/api/country/getCountries'),
      ]);
    };

    const storeEndpoint = async (): Promise<void> => {
      const response = await axios.post('/api/endpoint/storeEndpoint', endpoint);

      Object.assign(endpoint, response.data);
    };

    const cloneEndpoint = (): void => {
      endpoint.id = undefined as unknown as number;
    };

    const onTrafficTypeChange = (): void => {
      endpoint.advertisers_ids = [];

      fetchAdvertisersWrapper();

      endpoint.campaigns_ids = [];

      fetchCampaignsWrapper();

      endpoint.feeds_ids = [];

      fetchFeedsWrapper();

      if (isPop.value) {
        endpoint.bid_subject = BID_SUBJECT_CLICK;
      }

      if (isVideo.value) {
        endpoint.protocol = PROTOCOL_OPENRTB;

        endpoint.bid_subject = BID_SUBJECT_VIEW;

        endpoint.bid_type = BID_TYPE_PER_THOUSAND;

        endpoint.payable_view_registration = PAYABLE_VIEW_REGISTRATION_ON_BILLING;
      }
    };

    const onTrafficCategoryChange = (): void => {
      if (endpoint.traffic_category === TRAFFIC_CATEGORY_ADULT) {
        endpoint.only_mainstream_coverage = false;
      }
    };

    const onProtocolChange = (): void => {
      if (isDirectLink.value) {
        endpoint.direct = true;

        endpoint.traffic_type = TRAFFIC_TYPE_POP;

        endpoint.custom_response_provider = null;

        endpoint.max_qps = ENDPOINT_MAX_QPS_0;

        endpoint.region = REGION_EU;

        endpoint.bid_type = BID_TYPE_PER_ONE;

        endpoint.bid_subject = BID_SUBJECT_CLICK;

        return;
      }

      endpoint.direct = false;
    };

    const onUserChange = (): void => {
      endpoint.site_id = null;
    };

    const onProjectChange = (): void => {
      endpoint.user_id = undefined as unknown as number;

      onUserChange();

      onTrafficTypeChange();
    };

    const onCustomResponseProviderChange = (): void => {
      if (endpoint.custom_response_provider === CUSTOM_RESPONSE_PROVIDER_EXOCLICK) {
        endpoint.bid_type = BID_TYPE_PER_THOUSAND;
      }
    };

    const getDeclinedSubids = async (): Promise<void> => {
      declinedSubidsLoading.value = true;

      const response = await axios.post(
        '/api/subid/getDeclinedSubidsValuesByEndpointId',
        {
          endpoint_id: endpoint.id,
        },
      );

      declinedSubidsLoading.value = false;

      declinedSubids.value = response.data;

      showDeclinedSubids.value = true;
    };

    // TODO: Вынести в компонент Entry
    onMounted(() => {
      setInterval(() => {
        const hidableColumns = document.querySelectorAll('.hidable-column');

        hidableColumns.forEach((column) => {
          if (column.querySelector('.card')) {
            column.classList.remove('hidden');
          } else {
            column.classList.add('hidden');
          }
        });
      }, 500);
    });

    return {
      TRAFFIC_CATEGORY_MAINSTREAM,
      TRAFFIC_TYPE_POP,
      TRAFFIC_TYPE_PUSH,
      TRAFFIC_CATEGORIES,
      BID_SUBJECT_VIEW,
      BID_SUBJECTS,
      BID_TYPES,
      PAYABLE_VIEW_REGISTRATIONS,
      ENDPOINT_MAX_QPS,
      TARGETING_TYPE_DISABLED,
      TARGETING_TYPES,
      PROTOCOL_LEGACY,
      PROTOCOL_DIRECT_LINK,
      ENDPOINT_STATUSES,
      CUSTOM_RESPONSE_PROVIDERS,
      user,
      projectsLoading,
      projects,
      advertisersLoading,
      advertisers,
      campaignsLoading,
      campaigns,
      feedsLoading,
      feeds,
      countriesLoading,
      countries,
      endpoint,
      endpointErrorsMap,
      isPop,
      isPush,
      isVideo,
      isLegacy,
      isDirectLink,
      usersParams,
      sitesParams,
      trafficTypes,
      protocols,
      regions,
      declinedSubids,
      declinedSubidsLoading,
      declinedSubidsAsString,
      declinedSubidsAsStringSeparators,
      declinedSubidsAsStringSeparator,
      showDeclinedSubids,
      validateUuid,
      fetchEndpoint,
      storeEndpoint,
      cloneEndpoint,
      onProjectChange,
      onUserChange,
      onTrafficTypeChange,
      onTrafficCategoryChange,
      onProtocolChange,
      onCustomResponseProviderChange,
      getDeclinedSubids,
    };
  },
});
